import { SCORE_TREATMENT } from "@/defines/serverdefines"
import Bid from "./bid"
import Board from "./board"
import Card from "./card"
import Chair, { CHAIR } from "./chair"
import Contract, { CONTRACT, PENALTY } from "./contract"
import Trick from "./trick"

export enum HistoryFlag {
    NONE = 0,
    CLOSED_TABLE_RESULT = 1,
}

export class HistoryDTO {
    id = -1
    flag = HistoryFlag.NONE

    bidding: Bid[] = []
    tricks: Trick[] = []

    biddingClosed = false;
    cardsPlayedCount = 0

    playersName = ["", "", "", ""]
    playersId = [0, 0, 0, 0]
    playersLevel = [0, 0, 0, 0]

    declarer = new Chair()
    turn = new Chair()
    contract = new Contract()

    tricksEW = 0;
    tricksNS = 0;
    score = 0; // like: -420, +50

    completeDetails = false;

    // this block is only used by client for viewing details
    competitiveScoreNS = 0; // only used by client for viewing details
    competitiveScoreEW = 0; // only used by client for viewing details
    scoreTreatment = SCORE_TREATMENT.NOT_FINISHED;
    /** Name to show for this result in the field list; the server decides it, see boardResult(). */
    fieldName = "";
    playingDeclarer = new Chair(); // who actually played for the declarer.
    // end block

}

export enum HistoryTrickGet {
    BeforePlay,
    AfterPlay
}

export default class History extends HistoryDTO {

    board: Board

    constructor(board: Board) {
        super();
        this.board = board
        this.turn.set(board.dealer);
    }

    static fromObject(dto: HistoryDTO, withBoard: Board) {
        const h = new History(withBoard);
        h.id = dto.id;
        h.flag = dto.flag;

        for (const bid of dto.bidding)
            h.bidding.push(Bid.fromObject(bid));

        for (const trick of dto.tricks)
            h.tricks.push(Trick.fromObject(trick, withBoard));

        h.biddingClosed = dto.biddingClosed;
        h.cardsPlayedCount = dto.cardsPlayedCount;

        h.playersName = [...dto.playersName];
        h.playersId = [...dto.playersId];
        h.playersLevel = [...dto.playersLevel];

        h.declarer.set(dto.declarer);
        h.turn.set(dto.turn);
        h.contract.set(dto.contract);

        h.tricksEW = dto.tricksEW;
        h.tricksNS = dto.tricksNS;
        h.score = dto.score;

        h.competitiveScoreNS = dto.competitiveScoreNS; // only used by client for viewing details
        h.competitiveScoreEW = dto.competitiveScoreEW; // only used by client for viewing details
        h.scoreTreatment = dto.scoreTreatment;
        // Rows stored before fieldName was renamed still carry declarerName, and ResultDB
        // was already on version 3 then, so no upgrade cleared them out.
        h.fieldName = dto.fieldName ?? (dto as { declarerName?: string }).declarerName ?? "";
        h.playingDeclarer.set(dto.playingDeclarer);

        h.completeDetails = dto.completeDetails ?? h.determineIfComplete();

        return h;
    }

    /**
     * For the replayer we want a seperate instance of this history with it's own board.
     * This way a replay cannot alter a board result in any way.
     * @returns A cloned history with a cloned board
     */
    clone() {
        // first clone the board, then the history
        const b = Board.fromObject(this.board);
        return History.fromObject(this, b);
    }

    /**
     * Do we have all the data from the server, or not.
     */
    determineIfComplete() {
        // for this we check if we have all names of the players.
        let complete = this.playersName[0].length > 0
            && this.playersName[1].length > 0
            && this.playersName[2].length > 0
            && this.playersName[3].length > 0;
        return complete;
        // if (this.tricks.length === 1 && this.tricks[0].cards.length === 1
        //     && (this.scoreTreatment === SCORE_TREATMENT.NORMAL || this.scoreTreatment === SCORE_TREATMENT.NOT_FINISHED)
        //     && this.contract.isContract() && this.score !== 0) {
        //     complete = false;
        // }
        // return complete && this.completeDetails;
    }

    // gets reversed zero-based bid, or invalid bid
    getLastBid(i: number): Bid {
        if (i >= 0) {
            let lastIndex = this.bidding.length - 1;

            if (lastIndex >= i) {
                return this.bidding[lastIndex - i];
            }
        }
        return new Bid();
    }

    /**
     * Return the indexes of the card last played. Used in the getLastPlayed... methods
     * @returns { trickIndex: number, cardInTrickIndex: number }
     */
    private getLastPlayedIndexes(): { trickIndex: number, cardInTrickIndex: number } {
        const index = this.cardsPlayedCount - 1;
        const trickIndex = Math.floor(index / 4);
        const cardInTrickIndex = index % 4;
        return { trickIndex, cardInTrickIndex };
    }

    /**
     * Return the last played card.
     * @returns last played card
     */
    getLastPlayedCard(): Card {
        if (this.cardsPlayedCount > 0) {
            const { trickIndex, cardInTrickIndex } = this.getLastPlayedIndexes();
            return this.tricks[trickIndex].cards[cardInTrickIndex];
        }
        return new Card();
    }


    /**
     * Determines which chair played the last card (at cardsPlayedCount - 1)
     * This must be called BEFORE mutating any history state!
     */
    getLastPlayedTurn(): Chair {
        if (this.cardsPlayedCount === 0) {
            return new Chair(); // no cards played
        }

        const {
            trickIndex,
            cardInTrickIndex
        } = this.getLastPlayedIndexes();

        if (trickIndex >= this.tricks.length) {
            console.error('getLastCardPlayedBy: trick index out of bounds');
            return new Chair();
        }

        const trick = this.tricks[trickIndex];
        if (!trick.first.isPlayer()) {
            console.error('getLastCardPlayedBy: invalid trick.first');
            return new Chair();
        }

        // The chair that played the card at position cardInTrickIndex in the trick
        const playedBy = new Chair().set(trick.first);
        for (let i = 0; i < cardInTrickIndex; i++) {
            playedBy.setNext();
        }

        return playedBy;
    }

    reset() {
        this.declarer.setNone();
        this.contract.setNone();
        //		penalty     = PENALTY_NONE;
        this.tricksEW = 0;
        this.tricksNS = 0;
        this.score = 0;
        this.flag = HistoryFlag.NONE;

        // this block is only used by client for viewing details
        this.competitiveScoreNS = 0;
        this.competitiveScoreEW = 0;
        this.scoreTreatment = SCORE_TREATMENT.NOT_FINISHED;
        this.fieldName = "";
        // end block

        this.biddingClosed = false;
        this.cardsPlayedCount = 0;

        for (let i = 0; i < 4; i++) {
            this.playersId[i] = 0;
            this.playersName[i] = "";
            this.playersLevel[i] = 0;
        }

        this.bidding = [];
        this.tricks = [];
        // for (let i = 0; i < 13; i++) {
        // 	this.tricks[i].reset();
        // }
    }

    biddingOfChair(chair: Chair) {
        let bidding: Bid[] = [];
        let startIndex = (chair.index - this.board.dealer.index) % 4;

        for (let i = startIndex; i < this.bidding.length; i += 4) {
            bidding.push(this.bidding[i]);
        }

        return bidding;
    }

    getLead() {
        if (this.tricks.length > 0) {
            const cards = this.tricks[0].cards;

            if (cards.length > 0) {
                return cards[0];
            }
        }

        return new Card();
    }

    setLead(c: Card) {
        let t = this.tricks[0];

        if (t === undefined) {
            t = new Trick();
            this.tricks.push(t);
        }

        t.cards[0] = c;
        t.first = this.declarer.next();
    }

    setTricksDeclarer(n: number) {
        switch (this.declarer.index) {
            case CHAIR.NONE:
                console.error('History.setTricksDeclarer: declarer NOT set!');
                break;
            case CHAIR.NORTH:
            case CHAIR.SOUTH:
                this.tricksNS = n;
                this.tricksEW = 13 - n;
                break;
            case CHAIR.EAST:
            case CHAIR.WEST:
                this.tricksEW = n;
                this.tricksNS = 13 - n;
                break;
        }
    }

    get dummy() {
        if (this.contract.isContract()) {
            return this.declarer.partner();
        }
        return new Chair();
    }

    getPreviousTrick(ev: HistoryTrickGet) {
        let i = Math.min(12, this.tricksNS + this.tricksEW);

        if (ev === HistoryTrickGet.AfterPlay
            && this.tricks[i].first.isNone()
            && i > 0) // current trick is not yet played, so we give previous trick of last trick
            i--;

        if (i > 0) {
            return this.tricks[i - 1];
        }

        return undefined;
    }

    hasContractBid() {
        // search for a previous contract bid
        for (let i = this.bidding.length - 1; i >= 0; i--) {
            if (this.bidding[i].isContract()) {
                return true;
            }
        }
        return false;
    }

    getCurrentTrick(ev: HistoryTrickGet) {
        let i = Math.min(12, this.tricksNS + this.tricksEW);

        if (ev === HistoryTrickGet.AfterPlay
            && this.tricks[i].first.isNone()
            && i > 0) // Trick is not played, so we give previous trick
            i--;

        return this.tricks[i];
    }

    getTricks(chair?: Chair): number {
        if (chair) {
            if (chair.isNS()) {
                return this.tricksNS;
            } else if (chair.isEW()) {
                return this.tricksEW;
            }
            return 0;
        }
        return this.tricksNS + this.tricksEW;
    }

    get tricksRemaining() {
        return 13 - this.tricksNS - this.tricksEW;
    }

    get tricksDeclarer() {
        return this.getTricks(this.declarer);
    }

    set tricksDeclarer(n: number) {
        if (this.declarer.isNS()) {
            this.tricksNS = n;
            this.tricksEW = 13 - n;
        } else {
            this.tricksNS = 13 - n;
            this.tricksEW = n;
        }
    }

    get tricksDefender() {
        return this.getTricks(this.declarer.next());
    }

    isJumpBid(b?: Bid) {
        let startIndex = 0;

        if (b === undefined) {
            b = this.getLastBid(0);
            startIndex = 1;
        } else if (b.equals(this.getLastBid(0))) {
            startIndex = 1;
        }

        if (b.isContract()) {
            // search for a previous contract bid
            for (let i = startIndex; i < this.bidding.length; i++) {
                const previousBid = this.getLastBid(i);

                if (previousBid.isContract()) {
                    // ok, we got something. Now compare.
                    // getInt() will do height * 10 + suit, so it's easy to compare
                    return (b.getInt() - previousBid.getInt() > 10);
                }
            }
            // Nothing has been bidden, b is contract, so look at b
            return (b.height > 1);
        }
        return false;
    }

    // adjusts property contract based on current bidding
    // returns false, if bidding is invalid (not according to the rules).
    calculateContract() {
        let validBidding = true;
        let i = 0;
        let contractIndex = -1;
        let passCount = 0;
        let size = this.bidding.length;
        const contract = new Contract();
        let biddingClosed = false;

        while (i < size) {
            const b = this.bidding[i];

            if (b.isContract()) {
                if (b.greaterThan(contract)) {
                    contract.setFrom(b, PENALTY.NONE);
                    contractIndex = i;
                    passCount = 0;
                } else {
                    // invalid
                    validBidding = false;
                    break;
                }
            }
            else if (b.isDouble()) {
                if (contract.penalty !== PENALTY.NONE) {
                    // already penalty, impossible
                    validBidding = false;
                    break;
                }
                if (!contract.isContract()) {
                    validBidding = false;
                    break;
                }
                if (((i - contractIndex) != 1) && ((i - contractIndex) != 3)) {
                    // must be by opponents are directly after contract bid
                    validBidding = false;
                    break;
                }
                contract.penalty = PENALTY.DOUBLE;
                passCount = 0;
            }
            else if (b.isRedouble()) {
                if (contract.penalty != PENALTY.DOUBLE) {
                    // no penalty, impossible
                    validBidding = false;
                    break;
                }
                if (!contract.isContract()) {
                    validBidding = false;
                    break;
                }
                if (((i - contractIndex) != 2) && ((i - contractIndex) != 4)) {
                    // must be by leaders directly after contract bid
                    validBidding = false;
                    break;
                }
                contract.penalty = PENALTY.REDOUBLE;
                passCount = 0;

            } else if (b.isPass()) {
                passCount++;

                if ((passCount == 3) && contract.isContract()) {
                    biddingClosed = true;
                    break;
                } else if (passCount == 4) {
                    if (size != 4) {
                        validBidding = false;
                        break;
                    } else {
                        biddingClosed = true;
                        contract.setInt(CONTRACT.PASSEDOUT);
                        break;
                    }
                }
            }
            i++;
        }


        // is this the last bidding? as a final check
        if (i < size - 1) {
            // failed, there are more biddings left
            validBidding = false;
            console.error("Invalid bidding streak, too many bids after pass.", this);
        }

        if (validBidding) {
            // all good
            this.contract.set(contract);
            this.biddingClosed = biddingClosed;
        }

        return validBidding;
    }

    biddingFromIntegerString(s: string) {
        this.bidding = [];

        for (let l = 0; l < s.length; l += 3) {
            const tmp = s.substring(l, l + 3);
            const value = Number.parseInt(tmp);
            this.bidding.push(Bid.fromInt(value));
        }
    }

    biddingFromString(s: string) {
        // pe. PP1D1HP1SP2SP3SP4SPPXXXPPP
        // http://localhost:5173/replayer/index.html?username=JohnLi&lang=nl&boardnumber=21&dealer=N&vulnerability=NS&contract=2NT&declarer=E&lead=3H&north=J6.Q7.K43.AK8764&east=K987.AK92.9.QJT2&south=A543.JT83.QJT5.5&west=QT2.654.A8762.93&playernorth=BettyK&playereast=GerBos&playersouth=JohnLi&playerwest=MariBan&bidding=1CXXX1D2CPP2DP2NPPP&play=H3H4HQHKC2C5C9CKH7H2H8H5HJH6C4HACTS4C3C6CJS3D2CAC8CQS5D6S7SAS2S6&tricks=8&score=-120

        this.bidding = [];
        const su = s.toUpperCase()
        let p = "";

        for (let i = 0; i < su.length; i++) {
            p += su[i];

            if (i === su.length - 1 || "1234567PXR".indexOf(su[i + 1]) >= 0) {
                const alerted = p.endsWith('A');

                if (!alerted) {
                    // check for XX
                    if (p === "X" && i < su.length - 1 && su[i + 1] === 'X') {
                        p = "XX";
                        i += 1;

                        // check for XXX
                        if (i < su.length - 1 && su[i + 1] === 'X') {
                            p = "X";
                            i -= 1;
                        }
                    }
                }
                let b = new Bid();
                b.fromString(p);
                this.bidding.push(b);
                p = "";
            }
        }
        this.calculateContract();

        // console.debug('bidding from string', this.bidding);
    }

    tricksFromStringHistoryResult(s: string) {
        // clear tricks
        this.tricks = [];
        this.cardsPlayedCount = 0;
        let trick = new Trick();

        for (let i = 0; i < s.length; i++) {
            if (i % 4 == 0) {
                trick = new Trick();
                this.tricks.push(trick);
            }
            let card = Card.fromChar(s[i]);
            // finds card in the board and gives ref.
            card = this.board.getCardByInt(card.getInt()) ?? card;
            trick.cards.push(card);
            this.cardsPlayedCount++;

            if (i % 4 === 3) {
                trick.calcWinner(this.contract.suit);
            }
        }

        // we also set all first and winner props on all tricks.
        this.recalcTrickWinners();
    }


    tricksFromString(s: string) {
        // clear tricks
        this.tricks = [];
        this.cardsPlayedCount = 0;

        const su = s.toUpperCase();
        let trick = new Trick();

        for (let i = 0; i < su.length; i += 2) {
            if (i % 8 === 0) {
                trick = new Trick();
                this.tricks.push(trick);
            }
            const cs = su[i] + su[i + 1];
            let card = new Card();
            card.fromString(cs);
            // retrieve the card object from the board to make it reactive
            card = this.board.getCardByInt(card.getInt()) ?? card;
            trick.cards.push(card);
            this.cardsPlayedCount++;
        }

        // we also set all first and winner props on all tricks.
        this.recalcTrickWinners();
    }

    /**
     * Calculate all the winners and first props of all the tricks
     */
    recalcTrickWinners() {
        const trump = this.contract.suit;
        const turn = this.declarer.next();

        for (const trick of this.tricks) {
            trick.first.set(turn);
            trick.calcWinner(trump);
            turn.set(trick.winner);
        }
    }
}
